// Runtime compile security checks for CSS output.
// Blocks unsafe compiler results before any DOM style write can happen.
import type { LuisCompileResult } from '@lentystyle/compiler'
import { failClosedSecurityCheck, LuisSecurityError } from '@lentystyle/guard/runtime'
import { hasUnsafeCssPayload } from '@lentystyle/guard/shared'
import { buildRuntimeSecurityViolation, emitRuntimeSecurityViolation } from '../../security-events'

// Stop before style writes when compiler output violates guard policy.
export function assertCompileResultSafe(result: LuisCompileResult): void {
  assertCssTextSafe(result.css, 'compiled css', 'compiled-css-blocked')
  // Check dynamic observed rules before the observed plugin can render them.
  for (const rule of result.observedRules ?? []) {
    assertCssTextSafe(rule.rule, 'observed css', 'observed-css-blocked')
  }
  // Check initial observed rules because lazy mode may render them later.
  for (const rule of result.initialObservedRules ?? []) {
    assertCssTextSafe(rule.rule, 'initial observed css', 'observed-css-blocked')
  }
}

// Use guard/shared as a mandatory final gate before runtime DOM output.
function assertCssTextSafe(css: string, label: string, eventType: 'compiled-css-blocked' | 'observed-css-blocked'): void {
  try {
    const blocked: unknown = hasUnsafeCssPayload(css)
    // Treat malformed guard responses as security failures, not as allow decisions.
    if (typeof blocked !== 'boolean') {
      throw new Error(`Unexpected guard result for ${label}.`)
    } else if (blocked) {
      emitRuntimeSecurityViolation(buildRuntimeSecurityViolation(eventType, `Unsafe ${label} blocked before style output.`))
      throw new LuisSecurityError(`[LentyStyle][Security] Unsafe ${label} blocked before style output.`)
    } else {
      // Continue when guard explicitly allows the css text.
    }
  } catch (error) {
    // Preserve intentional security failures and normalize unexpected guard errors.
    if (error instanceof LuisSecurityError) {
      throw error
    } else {
      // Return the normalized fail-closed error path for unexpected guard failures.
      return failClosedSecurityCheck(`assertCssTextSafe:${label}`, error)
    }
  }
}
